'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const missions = [
    'Mengintegrasikan 500+ titik pertanian mengolah data untuk keperluan forecasting, analisis, dan penelitian',
    'Meningkatkan produktivitas pertanian melalui teknologi',
    'Membangun komunitas pengembang teknologi pertanian berbasis pembelajaran terbuka',
    'Menyediakan platform terintegrasi pengelolaan sistem pertanian cerdas',
  ];

  return (
    <section id="about" ref={sectionRef} className="relative bg-canopy blueprint-grid overflow-hidden">
      <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block opacity-20">
        <AppImage
          src="/assets/images/greenhouse.webp"
          alt="Greenhouse pertanian modern"
          fill
          className="object-cover object-left" />
        <div className="absolute inset-0 bg-gradient-to-l from-canopy via-canopy/60 to-canopy" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="reveal flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-chartreuse opacity-60" />
              <span className="annotation">TENTANG KAMI · SEJAK 2024</span>
            </div>
            <h2 className="reveal delay-100 font-mono font-black text-5xl md:text-7xl text-text-primary leading-none tracking-tight mb-8">
              AGRO<br />
              <span className="text-chartreuse">TEKNOLOGI</span><br />
              NUSANTARA
            </h2>
            <div className="reveal delay-200 space-y-5">
              <p className="text-text-muted text-base leading-relaxed">
                Perusahaan yang bergerak di bidang pertanian modern, hadir untuk
                membantu petani, pelaku usaha, dan komunitas agrikultur. Mengembangkan sistem
                pertanian yang lebih produktif, dan cerdas.
              </p>
              <p className="text-text-muted text-base leading-relaxed">
                Kami memadukan pertanian dengan <span className="text-chartreuse font-mono">Internet of Things (IoT)</span>,
                sistem monitoring real-time, kontrol jarak jauh. Dengan semangat
                <span className="text-chartreuse font-mono"> Inovasi</span>,
                <span className="text-chartreuse font-mono"> Solusi</span>, dan
                <span className="text-chartreuse font-mono"> Kreasi</span>,
                kami juga membangun platform terintegrasi
                yang dapat dimanfaatkan oleh petani, lembaga, organisasi, serta komunitas pengembang
                di sektor agrikultur.
              </p>
            </div>
            <div className="reveal  text-left delay-300 flex flex-col mt-8">
                <div className="border border-chartreuse/20 rounded-sm px-2 py-3 bg-pond/30">
                  <div className="font-mono font-bold text-chartreuse text-sm">Inovasi</div>
                  <div className="annotation text-text-muted/60 mt-0.5">Menghadirkan teknologi dan pendekatan terbaru</div>
                </div>
                <div className="border border-chartreuse/20 rounded-sm px-2 py-3 bg-pond/30">
                  <div className="font-mono font-bold text-chartreuse text-sm">Solusi</div>
                  <div className="annotation text-text-muted/60 mt-0.5">Pendekatan praktis dan berdampak</div>
                </div>
                <div className="border border-chartreuse/20 rounded-sm px-2 py-3 bg-pond/30">
                  <div className="font-mono font-bold text-chartreuse text-sm">Kreasi</div>
                  <div className="annotation text-text-muted/60 mt-0.5">Memberdayakan petani, pelaku usaha, dan masyarakat untuk berkembang bersama</div>
                </div>
            </div>
          </div>
          <div className="reveal delay-200 space-y-8">
            <div className="border border-chartreuse/20 rounded-sm p-8 bg-pond/40 relative">
              <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-chartreuse/30" />
              <div className="annotation mb-4">VISI</div>
              <blockquote className="font-mono text-text-primary text-lg leading-relaxed">
                Menjadi pelopor teknologi pertanian berbasis Smart Farming dan edukasi digital di Kalimantan
                yang berdampak nyata bagi petani masa depan.
              </blockquote>
            </div>
            <div className="border border-chartreuse/20 rounded-sm p-8 bg-pond/40 relative">
              <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-chartreuse/30" />
              <div className="annotation mb-4">MISI</div>
              <div className="flex flex-col gap-4">
                {missions.map((mission, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="font-mono font-black text-chartreuse text-xs mt-0.5 shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <p className="text-text-muted text-sm leading-relaxed">{mission}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* <div className="border border-chartreuse/15 rounded-sm p-6 bg-canopy/40">
              <div className="annotation mb-4">LOKASI KANTOR</div>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="font-mono text-[10px] text-chartreuse w-6 shrink-0">📍</span>
                  <span className="font-mono text-xs text-text-muted leading-snug">
                    Jl. Poros Samarinda Anggana, Sungai Meriam, Kec. Anggana, Kabupaten Kutai Kartanegara, Kalimantan Timur 75256
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-chartreuse w-6">📞</span>
                  <a href="tel:+6281253837777" className="font-mono text-xs text-text-muted hover:text-chartreuse transition-colors">
                    +62 812 5383 7777
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-chartreuse w-6">✉</span>
                  <a href="mailto:ptagroteknologinusantara@gmail.com" className="font-mono text-xs text-text-muted hover:text-chartreuse transition-colors">
                    ptagroteknologinusantara@gmail.com
                  </a>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 rule-line" />
    </section>
  );
}
